import React, {useEffect, useMemo, useState, useCallback} from "react";
import {DashbaordModal, DashboardWrraper} from "components/atoms/Dashboard/Dashboard.styles";
import {useDispatch, useSelector} from "react-redux";
import dash_menu from "assets/dash-menu.svg";
import total_revenue from "assets/total-revenue.svg";
import total_orders from "assets/total-orders.svg";
import RevenueLineChart from "components/atoms/Dashboard/Revenue";
import OrderSummary from "components/atoms/Dashboard/OrderSummary";
import OrderRequest from "components/atoms/Dashboard/OrderRequest";
import foodie from "assets/Home/foodie.svg";
import {IoIosArrowDown} from "react-icons/io";
import menuStoreService from "services/menu-storeService";
import analyticsService from "services/analyticsService";
import {useAuthContext} from "Context/authContext";
import {setDefaultStore, setSelectedStoreId} from "store/reducers/businessSlice";
import Select from "components/molecules/Select";
import CenterModal from "components/molecules/Modal/CenterModal";
import Button from "components/molecules/Button";
import {useNavigate} from "react-router-dom";

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userState = useSelector(state => state.business.user);
    const business = useSelector(state => state.business.businessId);
    const defaultStore = useSelector(state => state.business.defaultStore);
    const userType = useSelector(state => state.business.userType);
    const selectedStoreIdFromRedux = useSelector(state => state.business.selectedStoreId);
    const [notifications, setNotifications] = useState(false);

    const [selectedStore, setSelectedStore] = useState("");
    const [allStores, setAllStores] = useState([]);
    const [currentStoreId, setCurrentStoreId] = useState(selectedStoreIdFromRedux || defaultStore);
    const [dashboardSummary, setDashboardSummary] = useState({
        totalMenus: 0,
        totalRevenue: 0,
        totalOrders: 0,
    });
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('yearly');
    const [customDateRange, setCustomDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const {isLoggedIn, user, dashboardModal} = useAuthContext();
    const finalUser = user || userState;
    const businessIdFromAuth = finalUser?.businessId;
    const businessId = businessIdFromAuth || business;
    const {hasPermission} = useAuthContext("hasPermission");

    const stores = useMemo(() => {
        if (!allStores || allStores.length === 0) {
            console.log('🏪 Dashboard: No stores available for dropdown');
            return [];
        }
        
        if (userType === "restaurant_employee" && defaultStore) {
            const matchingStore = allStores.find(store => store._id === defaultStore);
            console.log('🏪 Dashboard: Employee mode - matching store:', matchingStore);
            return matchingStore
                ? [
                      {
                          value: matchingStore._id,
                          label: matchingStore.title || matchingStore.name,
                      },
                  ]
                : [];
        }
        
        const storeOptions = allStores.map(store => ({
            value: store._id,
            label: store.title || store.name,
        }));
        return storeOptions;
    }, [allStores, userType, defaultStore]);

    const cardData = useMemo(() => [
        {
            title: summaryLoading ? "..." : dashboardSummary.totalMenus.toString(),
            description: "Total Menus",
            imageSrc: dash_menu,
            altText: "dash_menu",
        },
        {
            title: summaryLoading ? "..." : dashboardSummary.totalRevenue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }),
            description: "Total Revenue",
            imageSrc: total_revenue,
            altText: "total_revenue",
        },
        {
            title: summaryLoading ? "..." : dashboardSummary.totalOrders.toString(),
            description: "Total Orders",
            imageSrc: total_orders,
            altText: "total_orders",
        },
    ], [dashboardSummary, summaryLoading]);

    const getDashboardSummary = useCallback(async () => {
        try {
            setSummaryLoading(true);
            // Simplified businessId logic
            const restaurantId = businessId || (Array.isArray(finalUser?.businessId) ? finalUser.businessId[0] : null);
            
            if (!restaurantId) {
                console.warn('📊 Dashboard: No restaurant ID available');
                setSummaryLoading(false);
                return;
            }
            
            const params = {
                restaurantId,
            };
            
            // Add storeId if available
            if (currentStoreId) {
                params.storeId = currentStoreId;
            }
            
            // Add time period filters
            if (selectedPeriod) {
                params.period = selectedPeriod;
            }
            
            // Add custom date range if selected
            if (selectedPeriod === 'custom') {
                if (customDateRange?.startDate) {
                    params.startDate = customDateRange.startDate;
                }
                if (customDateRange?.endDate) {
                    params.endDate = customDateRange.endDate;
                }
            }
            
            const res = await analyticsService.getDashboardSummary(params);
            
            if (res?.success && res?.data) {
                setDashboardSummary({
                    totalMenus: res.data.totalMenus || 0,
                    totalRevenue: res.data.totalRevenue.toFixed(2) || 0,
                    totalOrders: res.data.totalOrders || 0,
                });
            }
        } catch (error) {
            console.error('📊 Dashboard: Error fetching summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    }, [businessId, finalUser, currentStoreId, selectedPeriod, customDateRange]);

    const getAllStores = useCallback(async () => {
        try {
            const id = businessId || (Array.isArray(finalUser?.businessId) ? finalUser.businessId[0] : null);
            
            if (!id) {
                console.warn('🏪 Dashboard: No business ID available for fetching stores');
                return;
            }
            
            const res = await menuStoreService.getAllstores(id);
            
            const storesData = res?.items || res?.data?.items || res?.stores || [];
            setAllStores(storesData);

            if (storesData.length > 0) {
                const hasValidSelection = storesData.some(s => s._id === currentStoreId);
                const hasValidReduxSelection = storesData.some(s => s._id === selectedStoreIdFromRedux);

                let storeToSelect = null;

                if (hasValidReduxSelection) {
                    storeToSelect = selectedStoreIdFromRedux;
                } else if (hasValidSelection) {
                    storeToSelect = currentStoreId;
                } else {
                    const defaultStoreObj = storesData.find(s => s.default_branch);
                    storeToSelect = defaultStoreObj ? defaultStoreObj._id : storesData[0]._id;
                }

                setCurrentStoreId(storeToSelect);
                dispatch(setSelectedStoreId(storeToSelect));
                dispatch(setDefaultStore(storeToSelect));
            } else {
                console.log('🏪 Dashboard: No stores found');
            }
        } catch (error) {
            console.error('🏪 Dashboard: Error fetching stores:', error);
        }
    }, [businessId, finalUser, currentStoreId, selectedStoreIdFromRedux, dispatch]);


    useEffect(() => {
        if (isLoggedIn) {
            if (businessId || finalUser?.businessId?.[0]) {
                getAllStores();
                // getDashboardSummary() will be called by the second useEffect when currentStoreId is set
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, businessId, finalUser?.businessId]);

    // Fetch dashboard summary when store or period changes
    useEffect(() => {
        if (currentStoreId && (businessId || finalUser?.businessId?.[0])) {
            getDashboardSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStoreId, selectedPeriod, customDateRange]);

    // Update currentStoreId when selectedStoreIdFromRedux or defaultStore changes
    useEffect(() => {
        if (selectedStoreIdFromRedux) {
            setCurrentStoreId(selectedStoreIdFromRedux);
        } else if (defaultStore && !currentStoreId) {
            setCurrentStoreId(defaultStore);
        }
    }, [selectedStoreIdFromRedux, defaultStore, currentStoreId]);

    // Update selectedStore when allStores or currentStoreId changes
    useEffect(() => {
        const matchingStore = allStores?.find(store => store._id === currentStoreId);
        if (matchingStore) {
            const storeOption = {
                value: matchingStore._id,
                label: matchingStore.title || matchingStore.name,
            };
            setSelectedStore(storeOption);
        }
    }, [allStores, currentStoreId]);
    
    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
        if (period === 'custom') {
            setShowCustomDatePicker(true);
        } else {
            setShowCustomDatePicker(false);
            setCustomDateRange({ startDate: '', endDate: '' });
        }
    };
    
    const handleCustomDateChange = (field, value) => {
        setCustomDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    return (
        <>
            <CenterModal open={dashboardModal} noHead width="550">
                <DashbaordModal>
                    <h2>Complete Your Business Profile</h2>
                    <p>Your business profile is incomplete. Kindly complete it to proceed to the dashboard.</p>
                    <Button onClick={() => navigate("/business-setup")}>Complete profile</Button>
                </DashbaordModal>
            </CenterModal>
            <DashboardWrraper>
                <div className="dash-header">
                    <div className="heading">
                        <h2>Dashboard</h2>
                        <p>Hi, {user?.profile?.first_name}. Welcome back to Plates!</p>
                    </div>
                        <div className="period-filter">
                    <div className="period-tabs">
                        <button 
                            className={selectedPeriod === 'today' ? 'active' : ''}
                            onClick={() => handlePeriodChange('today')}
                        >
                            Today
                        </button>
                        <button 
                            className={selectedPeriod === 'weekly' ? 'active' : ''}
                            onClick={() => handlePeriodChange('weekly')}
                        >
                            Weekly
                        </button>
                        <button 
                            className={selectedPeriod === 'monthly' ? 'active' : ''}
                            onClick={() => handlePeriodChange('monthly')}
                        >
                            Monthly
                        </button>
                        <button 
                            className={selectedPeriod === 'yearly' ? 'active' : ''}
                            onClick={() => handlePeriodChange('yearly')}
                        >
                            Yearly
                        </button>
                        <button 
                            className={selectedPeriod === 'custom' ? 'active' : ''}
                            onClick={() => handlePeriodChange('custom')}
                        >
                            Custom
                        </button>
                        
                    </div>
                            {showCustomDatePicker && (
                        <div className="custom-date-picker">
                            <input
                                type="date"
                                value={customDateRange.startDate}
                                onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                                placeholder="Start Date"
                                max={customDateRange.endDate || undefined}
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={customDateRange.endDate}
                                onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                                placeholder="End Date"
                                min={customDateRange.startDate || undefined}
                            />
                        </div>
                    )}
            
                </div>
                    {/* {hasPermission("dashboard.selectstore") && (
                        <div className="dropdownWrap">
                            <Select
                                $bgWhite
                                value={selectedStore}
                                options={stores}
                                prefix={<img src={foodie} alt="foodie" />}
                                onChange={({target: {value}}) => {
                                    setCurrentStoreId(value.value);
                                    setSelectedStore(value);
                                    // Save selected store to Redux for persistence
                                    dispatch(setSelectedStoreId(value.value));
                                    dispatch(setDefaultStore(value.value));
                                }}
                                suffix={<IoIosArrowDown />}
                                noMargin
                            />
                        </div>
                    )} */}
                </div>
            
                <div className="business-details">
                    {cardData.map((item, index) => (
                        <div className="card" key={index}>
                            <div className="title">
                                <h2>{item.title}</h2>
                                <p>{item.description}</p>
                            </div>
                            <div className="image-wrap">
                                <img src={item.imageSrc} alt={item.altText} />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="graphs">
                    <OrderSummary 
                        selectedPeriod={selectedPeriod}
                        customDateRange={customDateRange}
                        currentStoreId={currentStoreId}
                    />
                    <RevenueLineChart 
                        selectedPeriod={selectedPeriod}
                        customDateRange={customDateRange}
                        currentStoreId={currentStoreId}
                    />
                </div>
                <OrderRequest 
                    selectedPeriod={selectedPeriod}
                    customDateRange={customDateRange}
                    currentStoreId={currentStoreId}
                />
            </DashboardWrraper>
        </>
    );
};

export default Dashboard;
